import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Tag,
  Space,
  Descriptions,
  Row,
  Col,
  Typography,
  Divider,
  Badge,
  Empty,
  Timeline,
  Tabs,
} from 'antd';
import DataTable from '@/components/DataTable';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Icons from '@/components/third-party/Icons';
import { PageRail } from '@/components/PageChrome';
import PageHeader from '@/components/PageHeader';
import { formatDateTime, formatDate } from '@/utils/datetime';
import { get_batch_ebook_list, get_batch_ebook } from '@/utils/api';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import { useMbmrAlias, useIngredientsAlias } from '@/hooks/useAppSetting';
import { exportBatchLogbookPdf } from '@/pages/Batch-Processing/batchLogbookExport';

const { Text } = Typography;
const { Option } = Select;

const BatchLogBook = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const mbmrAlias = useMbmrAlias();
  const ingredientsAlias = useIngredientsAlias();

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const pageBgColor = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const nestedBgColor = isDark ? defaultColors.palette.gray[700] : '#f9fafb';
  const [loading, setLoading] = useState(false);
  const [batchList, setBatchList] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [ebookData, setEbookData] = useState(null);

  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchBatchList();
  }, []);

  const fetchBatchList = async () => {
    setLoading(true);
    try {
      get_batch_ebook_list((response) => {
        if (response?.data?.status === 'success') {
          setBatchList(response.data.data || []);
        } else {
          toast.error(response?.data?.message || 'Failed to fetch batch list');
          setBatchList([]);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching batch list:', error);
      toast.error('Failed to fetch batch list');
      setBatchList([]);
      setLoading(false);
    }
  };

  const fetchEbookData = async (batchNumber) => {
    setLoading(true);
    try {
      get_batch_ebook(batchNumber, (response) => {
        if (response?.data?.status === 'success') {
          setEbookData(response.data.data || null);
        } else {
          toast.error(response?.data?.message || 'Failed to fetch batch e-book');
          setEbookData(null);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching batch e-book:', error);
      toast.error('Failed to fetch batch e-book');
      setEbookData(null);
      setLoading(false);
    }
  };

  const handleBatchChange = (batchNumber) => {
    setSelectedBatch(batchNumber);
    if (batchNumber) {
      fetchEbookData(batchNumber);
    } else {
      setEbookData(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Completed: 'success',
      'In Progress': 'processing',
      Pending: 'warning',
      Failed: 'error',
      Scheduled: 'default',
    };
    return colors[status] || 'default';
  };

  const handleExport = () => {
    if (!selectedBatch || !ebookData) {
      toast.warning('Select a batch and wait for the logbook to load before exporting');
      return;
    }
    setExporting(true);
    try {
      const ok = exportBatchLogbookPdf(ebookData, { mbmrAlias, ingredientsAlias });
      if (ok) {
        toast.success('Opening A4 PDF report — use Save as PDF in the print dialog');
      } else {
        toast.error('Failed to generate Batch LogBook PDF');
      }
    } catch (error) {
      console.error('Error exporting batch logbook PDF:', error);
      toast.error('Failed to export Batch LogBook PDF');
    } finally {
      setExporting(false);
    }
  };

  // Process Columns (for main table)
  const processColumns = [
    {
      title: 'Process',
      dataIndex: 'process_name',
      key: 'process_name',
      width: 200,
      render: (text, record) => (
        <div>
          <Text strong style={{ color: defaultColors.primary }}>
            {text}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.process_type}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Operations',
      dataIndex: 'operations',
      key: 'operations',
      width: 100,
      render: (operations) => <Tag>{operations?.length || 0}</Tag>,
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (text) => (text ? formatDateTime(text, 'DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 180,
      render: (text) => (text ? formatDateTime(text, 'DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      width: 150,
      render: (text) => (
        <Space>
          <Icons.User size={16} style={{ color: defaultColors.primary }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
  ];

  // Operation Columns (for nested table inside process)
  const operationColumns = [
    {
      title: 'Operation',
      dataIndex: 'operation_name',
      key: 'operation_name',
      width: 200,
      render: (text, record) => (
        <div>
          <Text strong style={{ color: defaultColors.primary }}>
            {text}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.operation_number} | {record.operation_type}
          </Text>
        </div>
      ),
    },
    {
      title: 'Equipment',
      dataIndex: 'machine_name',
      key: 'machine_name',
      width: 180,
      render: (text) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (text) => (text ? formatDateTime(text, 'DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 180,
      render: (text) => (text ? formatDateTime(text, 'DD-MMM-YYYY HH:mm') : '-'),
    },
    {
      title: 'Duration (min)',
      dataIndex: 'duration',
      key: 'duration',
      width: 120,
      render: (duration) => (duration ? `${duration} min` : '-'),
    },
    {
      title: 'Performed By',
      dataIndex: 'performed_by',
      key: 'performed_by',
      width: 150,
      render: (text) => (
        <Space>
          <Icons.User size={16} style={{ color: defaultColors.primary }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Verified By',
      dataIndex: 'verified_by',
      key: 'verified_by',
      width: 150,
      render: (text) =>
        text ? (
          <Space>
            <Icons.CheckCircle size={16} style={{ color: defaultColors.primary }} />
            <Text>{text}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  // Render Process Details (Operations inside process)
  const renderProcessOperations = (process) => {
    if (!process.operations || process.operations.length === 0) {
      return (
        <div className="p-4" style={{ backgroundColor: nestedBgColor }}>
          <Empty description="No operations recorded" />
        </div>
      );
    }

    return (
      <div className="p-4" style={{ backgroundColor: nestedBgColor }}>
        <div className="flex items-center gap-2 mb-3" style={{ color: textColor }}>
          <Icons.Workflow size={16} style={{ color: defaultColors.primary }} />
          <span style={{ fontSize: 14, fontWeight: 700 }}>Operations in {process.process_name}</span>
        </div>

        <DataTable
          height={null}
          columns={operationColumns}
          data={process.operations}
          pagination={false}
          size="small"
          rowKey="operation_id"
          bordered
          expandable={{
            expandedRowRender: (operation) => renderOperationDetails(operation),
            rowExpandable: (record) => true,
          }}
          scrollX={1400}
        />
      </div>
    );
  };

  // Render Operation Details (Sub-operations, ingredients, parameters, etc.)
  const renderOperationDetails = (operation) => {
    return (
      <div className="p-4" style={{ backgroundColor: cardBgColor }}>
        {/* Sub-operations Timeline */}
        {operation.sub_operations && operation.sub_operations.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-3" style={{ color: textColor }}>
              <Icons.Clock size={16} style={{ color: defaultColors.primary }} />
              <span style={{ fontSize: 14, fontWeight: 700 }}>Sub-Operations Timeline</span>
            </div>
            <Timeline
              mode="left"
              className="mb-4"
              items={operation.sub_operations.map((subOp) => ({
                color: subOp.status === 'Completed' ? 'green' : subOp.status === 'In Progress' ? defaultColors.primary : 'gray',
                dot:
                  subOp.status === 'Completed' ? (
                    <Icons.CheckCircle size={20} style={{ color: defaultColors.primary }} />
                  ) : subOp.status === 'In Progress' ? (
                    <Icons.Clock size={20} style={{ color: defaultColors.primary }} />
                  ) : (
                    <Icons.Circle className="text-gray-400" size={20} />
                  ),
                children: (
                  <Card size="small" className="mb-2">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div>
                            <Tag color="cyan">Step {subOp.sub_operation_id}</Tag>
                            <Text strong className="ml-2">
                              {subOp.sub_operation_name}
                            </Text>
                          </div>
                          <div>
                            <Badge status={getStatusColor(subOp.status)} text={subOp.status} />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Icons.User size={14} style={{ color: secondaryTextColor }} />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {subOp.performed_by}
                            </Text>
                          </div>
                        </Space>
                      </Col>
                      <Col span={12}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {subOp.start_time && (
                            <div className="flex items-center gap-1.5">
                              <Icons.Clock size={14} style={{ color: secondaryTextColor }} />
                              <Text style={{ fontSize: '12px' }}>
                                {formatDateTime(subOp.start_time, 'DD-MMM-YYYY HH:mm')}
                              </Text>
                            </div>
                          )}
                          {subOp.end_time && (
                            <div className="flex items-center gap-1.5">
                              <Icons.CheckCircle size={14} style={{ color: defaultColors.success }} />
                              <Text style={{ fontSize: '12px' }}>
                                {formatDateTime(subOp.end_time, 'DD-MMM-YYYY HH:mm')}
                              </Text>
                            </div>
                          )}
                          {subOp.remarks && (
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {subOp.remarks}
                              </Text>
                            </div>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                ),
              }))}
            />
            <Divider />
          </>
        )}

        {/* Ingredients, Parameters, Samples, etc. */}
        <Row gutter={16}>
          {/* Ingredients */}
          {operation.ingredients && operation.ingredients.length > 0 && (
            <Col span={24}>
              <Card size="small" className="mb-4" title={ingredientsAlias}>
                <DataTable
                  height={null}
                  size="small"
                  data={operation.ingredients}
                  pagination={false}
                  columns={[
                    { title: 'Material Code', dataIndex: 'material_code', key: 'material_code', width: 120 },
                    { title: 'Material Name', dataIndex: 'material_name', key: 'material_name', width: 200 },
                    { title: 'Required Qty', dataIndex: 'required_qty', key: 'required_qty', width: 100, render: (val) => val?.toFixed(2) },
                    { title: 'Actual Qty', dataIndex: 'actual_qty', key: 'actual_qty', width: 100, render: (val) => val?.toFixed(2) },
                    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80 },
                    {
                      title: 'Deviation',
                      dataIndex: 'deviation',
                      key: 'deviation',
                      width: 100,
                      render: (val) => (
                        <Tag color={val === 0 ? 'green' : val > 0 ? 'orange' : 'red'}>{val?.toFixed(2)}</Tag>
                      ),
                    },
                    {
                      title: 'Performed By',
                      dataIndex: 'performed_by',
                      key: 'performed_by',
                      width: 150,
                      render: (text) => (
                        <Space>
                          <Icons.User size={14} style={{ color: defaultColors.primary }} />
                          <Text>{text || 'N/A'}</Text>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </Col>
          )}

          {/* Parameters */}
          {operation.parameters && operation.parameters.length > 0 && (
            <Col span={24}>
              <Card size="small" className="mb-4" title="Process Parameters">
                <DataTable
                  height={null}
                  size="small"
                  data={operation.parameters}
                  pagination={false}
                  columns={[
                    { title: 'Parameter', dataIndex: 'parameter_name', key: 'parameter_name', width: 200 },
                    { title: 'Set Value', dataIndex: 'set_value', key: 'set_value', width: 100 },
                    { title: 'Actual Value', dataIndex: 'actual_value', key: 'actual_value', width: 100 },
                    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80 },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      width: 150,
                      render: (status) => (
                        <Badge
                          status={
                            status === 'Within Limit' ? 'success' : status === 'Out of Limit' ? 'error' : 'warning'
                          }
                          text={status}
                        />
                      ),
                    },
                    {
                      title: 'Performed By',
                      dataIndex: 'performed_by',
                      key: 'performed_by',
                      width: 150,
                      render: (text) => (
                        <Space>
                          <Icons.User size={14} style={{ color: defaultColors.primary }} />
                          <Text>{text || 'N/A'}</Text>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </Col>
          )}

          {/* Products */}
          {operation.products && operation.products.length > 0 && (
            <Col span={24}>
              <Card size="small" className="mb-4" title="Products/Outputs">
                <DataTable
                  height={null}
                  size="small"
                  data={operation.products}
                  pagination={false}
                  columns={[
                    { title: 'Product Code', dataIndex: 'product_code', key: 'product_code' },
                    { title: 'Product Name', dataIndex: 'product_name', key: 'product_name' },
                    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', render: (val) => val?.toFixed(2) },
                    { title: 'UOM', dataIndex: 'uom', key: 'uom' },
                    { title: 'Container ID', dataIndex: 'container_id', key: 'container_id' },
                  ]}
                />
              </Card>
            </Col>
          )}

          {/* Clearance Data - Only show if there's actual clearance data */}
          {operation.clearance_data &&
           (operation.clearance_data.previous_product ||
            operation.clearance_data.previous_batch ||
            operation.clearance_data.cleaning_type) && (
            <Col span={24}>
              <Card size="small" className="mb-4 bg-yellow-50 border-yellow-200" title="Line Clearance Information">
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Previous Product">{operation.clearance_data.previous_product || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Previous Batch">{operation.clearance_data.previous_batch || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Cleaning Type">{operation.clearance_data.cleaning_type || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Verified By">{operation.clearance_data.verified_by || 'N/A'}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  return (
    <div className="p-2.5 pt-5" style={{ backgroundColor: pageBgColor }}>
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        classNames={{ body: 'page-card-scroll' }}
        style={{ backgroundColor: cardBgColor, height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<Icons.BookOpen size={22} />}
            title={t('Batch LogBook')}
            subtitle={selectedBatch || undefined}
            description="The electronic logbook of one batch: its approvals, processes, operations and every recorded step."
            filters={
              <Select
                aria-label="Select batch number"
                placeholder="Select a batch to view e-book"
                value={selectedBatch}
                onChange={handleBatchChange}
                style={{ width: 320 }}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  (option.label || '').toLowerCase().includes(input.toLowerCase())
                }
                loading={loading}
                optionLabelProp="label"
              >
                {batchList.map((batch) => (
                  <Option
                    key={batch.batch_number}
                    value={batch.batch_number}
                    label={
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        <Text strong>{batch.batch_number}</Text>
                        <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                          {batch.recipe_name}
                        </Text>
                      </div>
                    }
                  >
                    <div style={{ overflow: 'hidden', width: '100%' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Text strong>{batch.batch_number}</Text>
                      </div>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {batch.recipe_name} | {batch.product_name}
                        </Text>
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            }
          />
        }
      >
        <PageRail
          items={[
            {
              key: 'export',
              icon: Icons.Download,
              label: 'Export PDF',
              onClick: handleExport,
              loading: exporting,
              disabled: !selectedBatch || !ebookData,
            },
          ]}
        />
        {/* Batch E-Book Content */}
        {ebookData && (
          <Tabs
            size="large"
            tabBarStyle={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9f9f9', borderRadius: '8px', padding: '2px 8px' }}
            items={[
              {
                key: 'details',
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Icons.ClipboardList size={14} />Batch details
                  </span>
                ),
                children: (
          <>
            {/* Batch Header Information */}
            <Card className="mb-4" bordered style={{ backgroundColor: cardBgColor, borderColor }} styles={{ body: { padding: 12 } }}>
              <Descriptions
                bordered
                size="small"
                column={3}
                styles={{
                  label: { backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f3f4f6', color: secondaryTextColor, fontWeight: 600, width: 150 },
                  content: { backgroundColor: cardBgColor, color: textColor },
                }}
              >
                <Descriptions.Item label="Batch Number" span={1}>
                  <Text strong style={{ color: defaultColors.primary }}>
                    {ebookData.batch_number}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label={`${mbmrAlias} Name`} span={1}>
                  {ebookData.recipe_name}
                </Descriptions.Item>
                <Descriptions.Item label={`${mbmrAlias} Code`} span={1}>
                  {ebookData.recipe_code}
                </Descriptions.Item>
                <Descriptions.Item label="Product Name" span={1}>
                  {ebookData.product_name}
                </Descriptions.Item>
                <Descriptions.Item label="Product Code" span={1}>
                  {ebookData.product_code}
                </Descriptions.Item>
                <Descriptions.Item label="Batch Size" span={1}>
                  {ebookData.batch_size} {ebookData.batch_size_uom}
                </Descriptions.Item>
                <Descriptions.Item label="Status" span={1}>
                  <Badge status={getStatusColor(ebookData.status)} text={ebookData.status} />
                </Descriptions.Item>
                <Descriptions.Item label="Start Time" span={1}>
                  {ebookData.start_time ? formatDateTime(ebookData.start_time, 'DD-MMM-YYYY HH:mm') : 'Not Started'}
                </Descriptions.Item>
                <Descriptions.Item label="End Time" span={1}>
                  {ebookData.end_time ? formatDateTime(ebookData.end_time, 'DD-MMM-YYYY HH:mm') : 'In Progress'}
                </Descriptions.Item>
                <Descriptions.Item label="Created By" span={1}>
                  {ebookData.created_by}
                </Descriptions.Item>
                <Descriptions.Item label="Scheduled Date" span={2}>
                  {ebookData.scheduled_date ? formatDate(ebookData.scheduled_date) : 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* BMR Approval - Horizontal Workflow */}
            {ebookData.bmr_approvals && (
              <>
                <div className="flex items-center gap-2 mb-2" style={{ color: textColor }}>
                  <Icons.Shield size={18} style={{ color: defaultColors.primary }} />
                  <span style={{ fontSize: 15, fontWeight: 700 }}>BMR Approval</span>
                </div>

                {(() => {
                  // Count non-null approvals
                  const approvals = [
                    ebookData.bmr_approvals.mbmr_creation,
                    ebookData.bmr_approvals.mbmr_approval,
                    ebookData.bmr_approvals.bmr_release_approval,
                    ebookData.bmr_approvals.bmr_release
                  ];
                  const nonNullCount = approvals.filter(a => a !== null).length;

                  // Calculate span based on count
                  const colSpan = nonNullCount === 0 ? 0 : 24 / nonNullCount;

                  // nothing signed yet: say so, instead of leaving a heading over a blank gap
                  if (nonNullCount === 0) {
                    return (
                      <div className="mb-4" style={{ padding: '16px 0', border: `1px dashed ${borderColor}`, borderRadius: 8 }}>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text style={{ color: secondaryTextColor }}>No approval signatures recorded for this batch yet</Text>} />
                      </div>
                    );
                  }

                  return (
                    <Card className="mb-4" style={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : defaultColors.palette.teal[50] }}>
                      <Row gutter={[16, 16]}>
                        {[
                          ['mbmr_creation', `${mbmrAlias} Creation`],
                          ['mbmr_approval', `${mbmrAlias} Approval`],
                          ['bmr_release_approval', 'BMR Release Approval'],
                          ['bmr_release', 'BMR Release'],
                        ].map(([key, label]) => {
                          const approval = ebookData.bmr_approvals[key];
                          if (!approval) return null;
                          const stampBorder = isDark ? defaultColors.palette.teal[400] : defaultColors.palette.teal[300];
                          const stampBg = isDark ? defaultColors.palette.teal[900] : defaultColors.palette.teal[50];
                          const stampInk = isDark ? defaultColors.palette.teal[300] : defaultColors.palette.teal[400];
                          return (
                            <Col key={key} span={colSpan}>
                              <Card
                                size="small"
                                title={<Text strong style={{ fontSize: '12px', color: textColor }}>{label}</Text>}
                                style={{ backgroundColor: cardBgColor, minHeight: '200px' }}
                              >
                                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                  <div style={{ marginBottom: '10px' }}>
                                    <Text strong style={{ color: textColor }}>{approval.user}</Text>
                                  </div>
                                  <div style={{ marginBottom: '15px' }}>
                                    <Text style={{ fontSize: '11px', color: secondaryTextColor }}>
                                      {formatDateTime(approval.timestamp, 'DD-MMM-YYYY HH:mm')}
                                    </Text>
                                  </div>
                                  <div style={{
                                    border: `2px solid ${stampBorder}`,
                                    borderRadius: '8px',
                                    padding: '30px 20px',
                                    backgroundColor: stampBg,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '150px'
                                  }}>
                                    {approval.stamp_url ? (
                                      <img
                                        src={approval.stamp_url}
                                        alt="Signature Stamp"
                                        style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                                        onError={(e) => {
                                          console.error('Image load error:', e.target.src);
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <>
                                        <div style={{ width: '60px', height: '40px', backgroundColor: stampInk, borderRadius: '50% 50% 0 0', marginBottom: '5px' }}></div>
                                        <div style={{ width: '80px', height: '30px', backgroundColor: stampInk, borderRadius: '12px' }}></div>
                                      </>
                                    )}
                                    <div style={{ marginTop: '10px', fontSize: '10px' }}>
                                      <Text style={{ color: secondaryTextColor }}>Digitally signed by</Text>
                                    </div>
                                    <Text strong style={{ fontSize: '11px', color: textColor }}>{approval.user}</Text>
                                  </div>
                                </div>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </Card>
                  );
                })()}
              </>
            )}

          </>
                ),
              },
              {
                key: 'processes',
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Icons.Workflow size={14} />Batch processes ({ebookData.processes?.length || 0})
                  </span>
                ),
                children: (
            <DataTable
              tableKey="batchLogbookProcesses"
              searchable
              searchPlaceholder="Search processes…"
              searchKeys={['process_name', 'process_type', 'status', 'assigned_to']}
              columnConfig
              showCount={false}
              emptyMessage="No processes recorded for this batch"
              height={null}
              minHeight={240}
              columns={processColumns}
              data={ebookData.processes}
              loading={loading}
              rowKey="process_id"
              pagination={{ pageSize: 15 }}
              bordered
              expandable={{
                expandedRowRender: renderProcessOperations,
                rowExpandable: (record) => record.operations && record.operations.length > 0,
              }}
              scrollX={1200}
            />
                ),
              },
            ]}
          />
        )}

        {/* No Selection */}
        {!selectedBatch && (
          <div className="text-center py-12">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  <Text className="text-lg" style={{ color: secondaryTextColor }}>
                    Please select a batch number to view e-book
                  </Text>
                </span>
              }
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default BatchLogBook;
